import { BannerNotification } from '@hologram-dimension/banner-notification';
import { Button } from '@hologram-dimension/button';
import { Icon } from '@hologram-dimension/icon';
import { Link } from '@hologram-dimension/link';
import { Panel } from '@hologram-dimension/panel';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import { Popover } from '@hologram-dimension/popover';
import useSimBilling from 'api/sims/useSimBilling';
import useSimDetails from 'api/sims/useSimDetails';
import useSimDeviceDetails from 'api/sims/useSimDeviceDetails';
import useSimProfiles from 'api/sims/useSimProfiles';
import clsx from 'clsx/lite';
import ApnInfoIcon from 'components/ApnInfoIcon/ApnInfoIcon';
import EmptyState from 'components/EmptyState/EmptyState';
import Field from 'components/Field/Field';
import PublicIpAddressField from 'components/PublicIpAddressField/PublicIpAddressField';
import useIsEuiccSim from 'hooks/SimDetails/useIsEuiccSim';
import { type FC, Fragment, useMemo } from 'react';
import useAnalytics from 'state/contexts/Analytics/useAnalytics';
import useOrganization from 'state/contexts/Organization/useOrganization';
import { apiDateToDateTime, formatDateTime, getPreflightDuration } from 'utils/dates';
import useSimUrl from 'utils/hooks/useSimUrl';
import EnumSimPage from 'utils/legacy/paths/simPage';
import styles from './SimDetailsPanel.module.css';

interface SimDetailsPanelProps {
  hologramSimId?: HologramSimId;
  simDetailBaseUrl: string;
}

const SimDetailsPanel: FC<SimDetailsPanelProps> = ({ hologramSimId, simDetailBaseUrl }) => {
  const {
    isLoading: isSimLoading,
    error: simError,
    simDetails,
    isValidating,
  } = useSimDetails(hologramSimId);
  const {
    isLoading: isDeviceLoading,
    error: deviceError,
    deviceDetails,
    isValidating: isDeviceValidating,
  } = useSimDeviceDetails(hologramSimId);
  const {
    showEuiccInfo,
    isLoading: showEuiccInfoLoading,
    error: showEuiccInfoError,
  } = useIsEuiccSim(hologramSimId);
  const {
    simProfiles,
    error: simProfilesError,
    isLoading: simProfilesIsLoading,
    isValidating: simProfilesIsValidating,
  } = useSimProfiles(hologramSimId);
  const { simBilling } = useSimBilling(hologramSimId);
  const {
    eid,
    msisdn,
    phoneNumber,
    simType,
    deactivationTime,
    // apn,
    // tunnelable,
    activationTime,
    preflightStartTime,
    expectedActivationTime,
    expectedPreflightDuration,
    // preflightDuration,
    profilesInstalledOnCard = 0,
    fallbackInstalled,
    fallbackEnabled,
    sku,
    networkStatus,
    // simFormat,
  } = simDetails;

  // If the SIM was ever (or is currently) in preflight
  const isPreflight =
    !!preflightStartTime || !!expectedActivationTime || networkStatus?.startsWith('TEST');

  // If the SIM has a legacy preflight configuration
  const isLegacyConfig =
    networkStatus === 'TEST-HIBERNATE' || networkStatus === 'TEST-AUTOACTIVATE';

  const isInactive = networkStatus === 'INACTIVE';

  const orgId = useOrganization();

  const profilesUrl = useSimUrl({
    simDetailBaseUrl,
    orgId,
    simId: hologramSimId,
    subpage: EnumSimPage.profiles,
  });

  const pricingUrl = useSimUrl({
    simDetailBaseUrl,
    orgId,
    simId: hologramSimId,
    subpage: EnumSimPage.pricing,
  });

  const { sendAnalyticsEvent } = useAnalytics();

  const isLoading = isSimLoading || showEuiccInfoLoading || simProfilesIsLoading || isDeviceLoading;
  const error = simError || showEuiccInfoError || simProfilesError || deviceError;

  const panelContents = useMemo(() => {
    if (error) {
      return (
        <PanelNotification
          variant="error"
          label="We ran into an issue loading your SIM devices details"
          actions={
            <Button
              variant="secondary"
              iconStart="Sync"
              onClick={() => {
                window.location.reload();
              }}
            >
              Reload page
            </Button>
          }
        >
          We will keep trying in the background or you can try to reload the page.
        </PanelNotification>
      );
    }

    if (isLoading) {
      return <PanelNotification>Loading...</PanelNotification>;
    }

    let profileContent;
    const ariaLabel = 'View profile'; // for Datadog event tracking

    if (profilesInstalledOnCard === 0) {
      profileContent = <EmptyState />;
    } else if (profilesInstalledOnCard === 1) {
      profileContent = (
        <Link type="inline" to={profilesUrl!} ariaLabel={ariaLabel}>
          {simProfiles[0].profileName}
        </Link>
      );
    } else if (!fallbackInstalled) {
      profileContent = (
        <Link type="inline" to={profilesUrl!} ariaLabel={ariaLabel}>
          {simProfiles.find((profile) => profile.installationState === 'ENABLED')?.profileName}
        </Link>
      );
    } else if (fallbackInstalled && !fallbackEnabled) {
      <>
        <Link type="inline" to={profilesUrl!} ariaLabel={ariaLabel}>
          {simProfiles.find((profile) => profile.installationState === 'ENABLED')?.profileName}
        </Link>
        {', '}
        {simProfiles
          .filter((profile) => profile.installationState !== 'ENABLED')
          .map((profile, index) => (
            <Fragment key={profile.iccid}>
              <Link type="inline" to={profilesUrl!} ariaLabel={ariaLabel}>
                {profile.profileName}
              </Link>
              {index + 2 < simProfiles.length && ', '}
            </Fragment>
          ))}
      </>;
    } else if (fallbackInstalled && fallbackEnabled) {
      profileContent = (
        <>
          {simProfiles.map((profile, index) => (
            <Fragment key={profile.iccid}>
              <Link type="inline" to={profilesUrl!} ariaLabel={ariaLabel}>
                {profile.profileName}
              </Link>
              {index + 1 < simProfiles.length && ', '}
            </Fragment>
          ))}
        </>
      );
    } else {
      profileContent = `${profilesInstalledOnCard} profiles`;
    }

    let coverageTypePopover;
    const onCoverageTypePopoverOpen = () => {
      if (sendAnalyticsEvent) sendAnalyticsEvent('Info - Coverage type');
    };
    switch (simType) {
      case 'Dual-Core SIM':
        coverageTypePopover = (
          <Popover
            id="coverage-type-popover"
            content="This SIM offers two mobile core networks, protected with network fallback. If one network fails, our technology seamlessly switches you to a backup. Hologram SIMs use eUICC technology to remotely update coverage by adding or removing profiles, without requiring you to swap the SIM in your device."
            onOpen={onCoverageTypePopoverOpen}
          >
            <Icon name="CircleInfo" fill="DdsColorInteractivePrimaryDefault" />
          </Popover>
        );
        break;
      case 'Multi-Core SIM':
        coverageTypePopover = (
          <Popover
            id="coverage-type-popover"
            content="This SIM offers multiple mobile core networks, protected with network fallback. If one network fails, our technology seamlessly switches you to a backup. Hologram SIMs use eUICC technology to remotely update coverage by adding or removing profiles, without requiring you to swap the SIM in your device."
            onOpen={onCoverageTypePopoverOpen}
          >
            <Icon name="CircleInfo" fill="DdsColorInteractivePrimaryDefault" />
          </Popover>
        );
        break;
      case 'Single-Core SIM':
        coverageTypePopover = (
          <Popover
            id="coverage-type-popover"
            content="Hologram SIMs use eUICC technology to remotely update coverage by adding or removing profiles, without requiring you to swap the SIM in your device."
            onOpen={onCoverageTypePopoverOpen}
          >
            <Icon name="CircleInfo" fill="DdsColorInteractivePrimaryDefault" />
          </Popover>
        );
        break;
      default:
        coverageTypePopover = null;
    }

    let outageProtectionContent;
    const outageProtectionPopover = (
      <Popover
        id="outage-protection-popover"
        content="Network fallback protects your coverage by allowing your SIM to automatically switch profiles if one profile loses connection."
        onOpen={() => {
          if (sendAnalyticsEvent) sendAnalyticsEvent('Info - Outage protection');
        }}
      >
        <Icon name="CircleInfo" fill="DdsColorInteractivePrimaryDefault" />
      </Popover>
    );
    if (!!fallbackEnabled && profilesInstalledOnCard >= 2) {
      outageProtectionContent = (
        <div className={styles.valueWithPopover}>
          <Icon name="Shield" fill="DdsColorPaletteTeal40" />
          <span>Fallback enabled</span>
          {outageProtectionPopover}
        </div>
      );
    } else if (!fallbackEnabled || profilesInstalledOnCard === 1) {
      outageProtectionContent = (
        <div className={styles.valueWithPopover}>
          <span>Dual-Core capable</span>
          <Link
            target="_blank"
            rel="noreferrer"
            type="inline"
            href="https://www.hologram.io/contact-sales/?utm_source=dashboard&utm_medium=application&utm_content=sim_details_dual_core_capable"
          >
            Contact Sales
          </Link>
          {outageProtectionPopover}
        </div>
      );
    }

    let activationBannerContent;
    if (networkStatus?.endsWith('HIBERNATE')) {
      activationBannerContent = (
        <BannerNotification variant="note">
          Your SIM is on a legacy preflight configuration. After it uses test data, it will be
          paused until you manually activate the SIM.
        </BannerNotification>
      );
    } else if (networkStatus?.endsWith('AUTOACTIVATE')) {
      activationBannerContent = (
        <BannerNotification variant="note">
          Your SIM is on a legacy preflight configuration. As soon as it connects to a network for
          the first time it will automatically activate.
        </BannerNotification>
      );
    } else if (getPreflightDuration(expectedPreflightDuration) === 'Unlimited') {
      activationBannerContent = (
        <BannerNotification variant="note">
          Your SIM will be activated after 100 KB of data usage, sending 10 outgoing SMS messages,
          or you choose to manually activate the SIM.
        </BannerNotification>
      );
    } else {
      activationBannerContent = (
        <BannerNotification variant="note">
          Your SIM will be activated after your pre-deployment testing duration, 100 KB of data
          usage, or sending 10 outgoing SMS messages, whichever comes first.
        </BannerNotification>
      );
    }

    return (
      <>
        {networkStatus?.startsWith('DEAD') && deactivationTime && (
          <div className={styles.panelSection}>
            <div className={styles.panelSectionHeading}>Deactivation</div>
            <Field
              label="Deactivated"
              value={
                <Popover
                  id="deactivated-time"
                  content={formatDateTime(deactivationTime, '12hrlocal')}
                >
                  <span>{formatDateTime(deactivationTime, '24hrutc')}</span>
                </Popover>
              }
            />
          </div>
        )}
        <div className={styles.panelSection}>
          <div className={styles.panelSectionHeading}>Coverage configuration</div>
          <div className={styles.panelSectionColumns}>
            <div className={styles.panelSectionColumn}>
              <Field
                label="Coverage type"
                value={
                  <div className={styles.valueWithPopover}>
                    <span>{simType}</span>
                    {coverageTypePopover}
                  </div>
                }
              />
              {!!fallbackInstalled && (
                <Field label="Outage protection" value={outageProtectionContent} />
              )}
              {!!fallbackInstalled && !!fallbackEnabled && (
                <Field label="Fallback timer" value="15 minutes" />
              )}
            </div>
            <div className={styles.panelSectionColumn}>
              <Field
                label={profilesInstalledOnCard > 1 ? 'Installed profiles' : 'Installed profile'}
                value={profileContent}
              />
              <Field label="Required APN" value={<ApnInfoIcon profiles={simProfiles} />} />
            </div>
          </div>
        </div>
        <div className={styles.panelSection}>
          <div className={styles.panelSectionHeading}>Identifying details</div>
          <div className={styles.panelSectionColumns}>
            <div className={styles.panelSectionColumn}>
              <Field label="SIM ID" value={hologramSimId} valueClassname={styles.numeric} />
              <Field label="SIM name" value={deviceDetails.deviceName} />
              <Field
                label="SKU"
                value={sku ? `Printed as ${sku}` : null}
                emptyStateText="Not available"
              />
            </div>
            <div className={styles.panelSectionColumn}>
              {showEuiccInfo && (
                <Field label="eUICCID (EID)" value={eid} valueClassname={styles.numeric} />
              )}
              <Field
                label={simProfiles.length > 1 ? 'Enabled profile MSISDN' : 'MSISDN'}
                value={msisdn}
                valueClassname={styles.numeric}
              />
            </div>
          </div>
        </div>
        <div className={styles.panelSection}>
          <div className={styles.panelSectionHeading}>
            {isPreflight ? 'Activation and pre-deployment testing' : 'Activation'}
          </div>
          <div className={styles.panelSectionColumns}>
            <div className={styles.panelSectionColumn}>
              {(activationTime || isInactive) && (
                <Field
                  label="Activated date"
                  value={
                    activationTime && (
                      <div className={styles.valueWithPopover}>
                        <span>
                          <Popover
                            id="activation-time"
                            content={formatDateTime(activationTime, '12hrlocal')}
                            className={styles.numeric}
                          >
                            <span className={styles.numeric}>
                              {formatDateTime(activationTime, '24hrutc')}
                            </span>
                          </Popover>
                        </span>
                        {isPreflight && (
                          <Popover
                            id="activation-time-info"
                            content="The date your SIM was activated after reaching your pre-deployment testing duration, 100 KB of data usage, or sending 10 outgoing SMS messages, whichever came first."
                            onOpen={() => {
                              if (sendAnalyticsEvent) sendAnalyticsEvent('Info - Activated date');
                            }}
                          >
                            <Icon name="CircleInfo" fill="DdsColorInteractivePrimaryDefault" />
                          </Popover>
                        )}
                      </div>
                    )
                  }
                />
              )}
              {isPreflight && (
                <Field
                  label="Entered testing"
                  value={
                    <Popover
                      id="activation-time"
                      content={formatDateTime(preflightStartTime, '12hrlocal')}
                    >
                      <span>{formatDateTime(preflightStartTime, '24hrutc')}</span>
                    </Popover>
                  }
                />
              )}
              {isPreflight && isLegacyConfig && (
                <Field
                  label="Test configuration"
                  value={networkStatus?.endsWith('HIBERNATE') ? 'Hibernate' : 'Autoactivate'}
                />
              )}
              {isPreflight && !isLegacyConfig && (
                <Field
                  label="Testing duration"
                  value={getPreflightDuration(expectedPreflightDuration)}
                />
              )}
              {!activationTime && expectedActivationTime && !isLegacyConfig && (
                <Field
                  label="Target activation date"
                  value={
                    // TODO: DAN TAKE A LOOK AT LUXON FOR DURATION
                    <Popover
                      id="activation-time"
                      content={formatDateTime(expectedActivationTime, '12hrlocal')}
                    >
                      <span>
                        {formatDateTime(expectedActivationTime, '24hrutc')} (
                        {apiDateToDateTime(expectedActivationTime)?.toRelative({ unit: 'days' })})
                      </span>
                    </Popover>
                  }
                />
              )}
            </div>
          </div>
          {!activationTime && !isInactive && activationBannerContent}
        </div>
        <div className={styles.panelSection}>
          <div className={styles.panelSectionHeading}>Additional configuration</div>
          <div className={styles.panelSectionColumns}>
            <div className={styles.panelSectionColumn}>
              <PublicIpAddressField hologramSimId={hologramSimId} />
              <Field
                label="Outbound SMS limit"
                value={
                  <div className={clsx(styles.valueWithLink, styles.numeric)}>
                    <span>{isPreflight ? '10' : simBilling.smsLimit}</span>
                    <span>
                      <Link
                        href="https://support.hologram.io/hc/en-us/articles/17463264768151-Set-a-device-s-SMS-limit?utm_source=dashboard&utm_medium=application&utm_content=sim_details_sms_limit"
                        target="_blank"
                        rel="noreferrer"
                        type="inline"
                        ariaLabel="Learn more - SMS limit"
                      >
                        Learn more
                      </Link>
                    </span>
                  </div>
                }
              />
            </div>
            <div className={styles.panelSectionColumn}>
              <Field
                label="Phone number"
                valueClassname={styles.numeric}
                value={
                  <div className={styles.valueWithLink}>
                    {!phoneNumber && <span className={styles.diminished}>None</span>}
                    <Link
                      to={pricingUrl!}
                      type="inline"
                      ariaLabel={
                        phoneNumber
                          ? 'Phone number - Pricing link'
                          : 'Purchase phone number - Pricing link'
                      }
                    >
                      {phoneNumber || 'Purchase phone number'}
                    </Link>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </>
    );
  }, [
    activationTime,
    deactivationTime,
    deviceDetails.deviceName,
    eid,
    error,
    expectedActivationTime,
    expectedPreflightDuration,
    fallbackEnabled,
    fallbackInstalled,
    hologramSimId,
    isInactive,
    isLegacyConfig,
    isLoading,
    isPreflight,
    msisdn,
    networkStatus,
    phoneNumber,
    preflightStartTime,
    pricingUrl,
    profilesInstalledOnCard,
    profilesUrl,
    sendAnalyticsEvent,
    showEuiccInfo,
    simBilling.smsLimit,
    simProfiles,
    simType,
    sku,
  ]);

  return (
    <Panel
      header={
        simDetails.networkStatus?.startsWith('DEAD') ? 'Last known SIM details' : 'SIM details'
      }
      noContentPadding
      isLoading={isLoading || isValidating || simProfilesIsValidating || isDeviceValidating}
    >
      {panelContents}
    </Panel>
  );
};

export default SimDetailsPanel;
